import { useState, useEffect, useCallback } from "react";
import { supabase, BRAND, INK, MUTED, FAINT, LINE, text, card, input, btn, btnSm, chip, linkBtn, errorText, savedText, segWrap, segBtn, oneLine, ROW_LINE } from "./constants";
import { ConfirmBar } from "./shared";
import { useIsMobile } from "./lib/responsive";

// ─── USUARIOS DE UN CLIENTE (solo admin, ficha del cliente) ───────────────────
// Quién de la empresa entra en rutment.com (user_clients) y la invitación.
// Todo pasa por /api/invite-client, con la sesión del admin: los emails están
// en Supabase Auth, que el navegador no puede leer. El correo lleva un enlace
// a /welcome para poner la contraseña; la contraseña nunca va por correo.

const fmt = (d, lang) => d ? new Date(d).toLocaleDateString(lang === "es" ? "es-ES" : "en-GB", { day: "2-digit", month: "short", year: "numeric" }) : null;

const call = async (method, query = "", body) => {
  const { data: { session } } = await supabase.auth.getSession();
  const jwt = session?.access_token;
  if (!jwt) throw new Error("No session");
  const r = await fetch(`/api/invite-client${query}`, {
    method,
    headers: { Authorization: `Bearer ${jwt}`, ...(body ? { "Content-Type": "application/json" } : null) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
  return data;
};

export default function ClientUsers({ lang, clientId, clientName }) {
  const es = lang === "es";
  const isMobile = useIsMobile();
  const pad = isMobile ? 16 : 20;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const [form, setForm] = useState(null); // { email, name, lang } mientras se invita
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setLoadErr("");
    try {
      const { users: list } = await call("GET", `?clientId=${clientId}`);
      setUsers(list || []);
    } catch (e) {
      setLoadErr(es ? `No se pudieron cargar los usuarios: ${e.message}` : `Couldn't load the users: ${e.message}`);
    }
    setLoading(false);
  }, [clientId, es]);
  useEffect(() => { load(); }, [load]);

  const open = (u = null) => {
    setErr(""); setDone(""); setConfirming(false);
    setForm(u ? { email: u.email || "", name: u.name || (u.email || "").split("@")[0], lang: "en", greeting: "", resend: true } : { email: "", name: "", lang: "en", greeting: "" });
  };
  const close = () => { setForm(null); setConfirming(false); setErr(""); };
  // En español el saludo lleva género ("¡Bienvenido!" / "¡Bienvenida!"): se elige, nunca se deduce del nombre
  const ready = form && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) && form.name.trim() && (form.lang !== "es" || form.greeting);

  const send = async () => {
    setSending(true); setErr("");
    try {
      await call("POST", "", { clientId, email: form.email.trim(), name: form.name.trim(), lang: form.lang, greeting: form.greeting });
      setDone(es ? `Invitación enviada a ${form.email.trim()}.` : `Invitation sent to ${form.email.trim()}.`);
      setForm(null); setConfirming(false);
      load();
    } catch (e) {
      setErr(e.message);
      setConfirming(false);
    }
    setSending(false);
  };

  const status = (u) => {
    const entered = u.acceptedAt || u.lastSignInAt;
    if (entered) return es ? `Ha entrado · ${fmt(u.lastSignInAt || u.acceptedAt, lang)}` : `Signed in · ${fmt(u.lastSignInAt || u.acceptedAt, lang)}`;
    if (u.invitedAt) return es ? `Invitado el ${fmt(u.invitedAt, lang)} · no ha entrado` : `Invited ${fmt(u.invitedAt, lang)} · not signed in yet`;
    return es ? "No ha entrado" : "Not signed in yet";
  };

  const onFocus = e => { e.target.style.borderColor = BRAND; };
  const offFocus = e => { e.target.style.borderColor = LINE; };
  const field = { ...input, ...(isMobile ? { height: 44, fontSize: 16 } : null) };

  return (
    <div style={{ ...card, padding: 0, overflow: "hidden" }}>
      <div style={{ padding: `16px ${pad}px 12px`, display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <span style={text.cardTitle}>{es ? "Usuarios" : "Users"} ({users.length})</span>
        {!form && <button onClick={() => open()} style={{ ...linkBtn, ...(isMobile ? { minHeight: 40 } : null) }}>{es ? "Invitar usuario" : "Invite user"}</button>}
      </div>
      {loading && <div style={{ padding: `0 ${pad}px 12px`, ...text.body, color: MUTED }}>{es ? "Cargando..." : "Loading..."}</div>}
      {loadErr && <div style={{ padding: `0 ${pad}px 12px`, ...errorText }}>{loadErr}</div>}
      {!loading && !loadErr && users.length === 0 && (
        <div style={{ padding: `0 ${pad}px 12px`, ...text.body, color: MUTED }}>
          {es ? `Nadie de ${clientName || "este cliente"} tiene acceso a rutment.com todavía.` : `Nobody from ${clientName || "this client"} has access to rutment.com yet.`}
        </div>
      )}
      {users.map(u => {
        const entered = !!(u.acceptedAt || u.lastSignInAt);
        return (
          <div key={u.userId} style={{ display: "flex", alignItems: "center", gap: 8, padding: `10px ${pad}px`, borderTop: `1px solid ${ROW_LINE}` }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: INK, ...oneLine }} title={u.email || ""}>{u.name || u.email || (es ? "Usuario" : "User")}</div>
              <div style={{ ...text.meta, ...oneLine }}>{u.name && u.email ? `${u.email} · ` : ""}{status(u)}</div>
            </div>
            {entered
              ? <span style={chip(false)}>{es ? "Activo" : "Active"}</span>
              : !form && u.email && <button onClick={() => open(u)} style={{ ...linkBtn, ...(isMobile ? { minHeight: 40 } : null) }}>{es ? "Reenviar" : "Resend"}</button>}
          </div>
        );
      })}

      {done && <div style={{ padding: `10px ${pad}px`, borderTop: `1px solid ${ROW_LINE}`, ...savedText }}>✓ {done}</div>}

      {form && (
        <div style={{ padding: `12px ${pad}px ${pad}px`, borderTop: `1px solid ${ROW_LINE}` }}>
          {!form.resend && <>
            <div style={{ ...text.label, marginBottom: 8 }}>{es ? "Invitar usuario" : "Invite user"}</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder={es ? "Nombre y apellidos" : "Full name"}
                onFocus={onFocus} onBlur={offFocus} style={field} disabled={confirming} />
              <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@empresa.com" type="email"
                onFocus={onFocus} onBlur={offFocus} style={field} disabled={confirming} />
            </div>
          </>}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
            <span style={text.meta}>{es ? "Idioma del correo" : "Email language"}</span>
            <div style={{ ...segWrap, display: "inline-flex" }}>
              {["en", "es"].map(l => (
                <button key={l} onClick={() => setForm(f => ({ ...f, lang: l }))} style={{ ...segBtn(form.lang === l), height: isMobile ? 40 : 28, padding: "0 12px", fontSize: 12 }}>{l.toUpperCase()}</button>
              ))}
            </div>
          </div>
          {form.lang === "es" && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
              <span style={text.meta}>{es ? "Saludo" : "Greeting"}</span>
              <div style={{ ...segWrap, display: "inline-flex" }}>
                {[["m", "¡Bienvenido!"], ["f", "¡Bienvenida!"]].map(([v, l]) => (
                  <button key={v} onClick={() => setForm(f => ({ ...f, greeting: v }))} disabled={confirming} style={{ ...segBtn(form.greeting === v), height: isMobile ? 40 : 28, padding: "0 12px", fontSize: 12 }}>{l}</button>
                ))}
              </div>
            </div>
          )}
          {err && <div style={{ ...errorText, marginTop: 10 }}>{err}</div>}
          {confirming ? (
            <ConfirmBar
              message={es
                ? `${form.resend ? "¿Reenviar la invitación" : "¿Invitar"} a ${form.email.trim()} a ${clientName}? Le llega un correo en ${form.lang === "es" ? "español" : "inglés"} con un enlace para poner su contraseña.`
                : `${form.resend ? "Resend the invitation" : "Invite"} ${form.email.trim()} to ${clientName}? They get an email in ${form.lang === "es" ? "Spanish" : "English"} with a link to set their password.`}
              actionLabel={sending ? (es ? "Enviando..." : "Sending...") : (es ? "Enviar invitación" : "Send invitation")}
              cancelLabel={es ? "Cancelar" : "Cancel"}
              onConfirm={sending ? () => {} : send}
              onCancel={() => setConfirming(false)}
              style={{ marginTop: 10 }} />
          ) : (
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button onClick={() => setConfirming(true)} disabled={!ready}
                style={{ ...(ready ? btn.primary : btn.disabled), ...btnSm, ...(isMobile ? { flex: 1, minHeight: 44 } : null) }}>
                {form.resend ? (es ? "Reenviar" : "Resend") : (es ? "Invitar" : "Invite")}
              </button>
              <button onClick={close} style={{ ...btn.ghost, ...btnSm, color: MUTED, ...(isMobile ? { flex: 1, minHeight: 44 } : null) }}>{es ? "Cancelar" : "Cancel"}</button>
            </div>
          )}
          <div style={{ ...text.meta, color: FAINT, marginTop: 8 }}>
            {es ? "El enlace caduca en 24 horas. Si el email es de otro cliente o de un candidato del portal, no se envía nada." : "The link expires in 24 hours. If the email belongs to another client or a portal candidate, nothing is sent."}
          </div>
        </div>
      )}
    </div>
  );
}
